import { Button, Card, Icon, Input, InputWrapper } from "@components";
import { PromotionTypeEnum } from "@utilities/enums";
import { formatPrice } from "@utilities/functions";
import { ErrorType, Promotion } from "@utilities/types";
import { FC, useCallback, useMemo, useState } from "react";

interface Props {
    error: ErrorType | null;
    promotion: Promotion | null;
    totalToPay: number;
    onApplyPromotion: (code: string) => void;
    onRemovePromotion?: () => void;
}

export const PromotionSection: FC<Props> = ({
    error,
    promotion,
    totalToPay,
    onApplyPromotion,
    onRemovePromotion,
}) => {

    const [code, setCode] = useState<string>('');

    const remiseAmount = useMemo(() => {
        if (!promotion || !promotion.isActive) return null;

        if (promotion.type === PromotionTypeEnum.PERCENTAGE) {
            const discount = totalToPay * (promotion.value / 100);
            return {
                label: `${promotion.value}%`,
                value: discount
            };
        }
        return {
            label: `${formatPrice(promotion.value)}`,
            value: promotion.value
        };
    }, [promotion, totalToPay]);

    const newTotalToPay = useMemo(() => {
        if (!promotion || !promotion.isActive) return totalToPay;

        return promotion.type === PromotionTypeEnum.PERCENTAGE
            ? totalToPay - (totalToPay * (promotion.value / 100))
            : totalToPay - promotion.value;
    }, [promotion, totalToPay]);

    const handleApplyCode = useCallback(() => {
        if (!code) return;

        onApplyPromotion(code);
        setCode('');
    }, [code, onApplyPromotion]);

    const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            handleApplyCode();
        }
    }, [handleApplyCode]);

    return (
        <Card title="Promotion / Remise">
            <div className="flex flex-col gap-4">
                {/* Affichage si promotion appliquée */}
                {promotion && promotion.isActive ? (
                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-4 space-y-3">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                                    <Icon iconName="Tag" size={16} className="text-white" />
                                </div>
                                <div>
                                    <p className="font-semibold text-green-800">Code appliqué</p>
                                    <p className="text-sm text-green-600">{promotion.code}</p>
                                </div>
                            </div>
                            {onRemovePromotion && (
                                <button
                                    onClick={onRemovePromotion}
                                    className="text-gray-400 hover:text-red-500 transition-colors"
                                >
                                    <Icon iconName="X" size={20} />
                                </button>
                            )}
                        </div>

                        <div className="border-t border-green-200 pt-3 space-y-2">
                            <div className="flex justify-between text-sm">
                                <span className="text-gray-600">Sous-total</span>
                                <span className="text-gray-800">{formatPrice(totalToPay)}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                                <span className="text-green-600 font-medium">
                                    Remise ({remiseAmount?.label})
                                </span>
                                <span className="text-green-600 font-medium">
                                    -{formatPrice(remiseAmount?.value || 0)}
                                </span>
                            </div>
                            <div className="border-t border-green-200 pt-2 flex justify-between">
                                <span className="font-bold text-gray-800">Nouveau total</span>
                                <span className="font-bold text-lg text-green-700">
                                    {formatPrice(newTotalToPay)}
                                </span>
                            </div>
                        </div>

                        {promotion.isSingleUse && (
                            <div className="flex items-center gap-1 text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">
                                <Icon iconName="AlertCircle" size={12} />
                                <span>Code à usage unique</span>
                            </div>
                        )}
                    </div>
                ) : (
                    /* Formulaire de saisie du code */
                    <div className="flex flex-col sm:flex-row items-start sm:items-end gap-2">
                        <div className="flex-1 w-full">
                            <InputWrapper label="Entrer le code promo">
                                <Input
                                    placeholder="ex: PROMO2025"
                                    value={code}
                                    onChange={(e) => setCode(e.target.value.toUpperCase())}
                                    onKeyDown={handleKeyDown}
                                    className="w-full"
                                />
                            </InputWrapper>
                        </div>

                        <Button
                            variant="success"
                            className="rounded-lg px-6 w-full sm:w-auto"
                            onClick={handleApplyCode}
                            disabled={!code.trim()}
                        >
                            <Icon iconName="Tag" size={16} className="mr-2" />
                            Appliquer
                        </Button>
                    </div>
                )}

                {/* Message d'erreur */}
                {error && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
                        <Icon iconName="AlertCircle" size={18} className="text-red-500 mt-0.5 flex-shrink-0" />
                        <p className="text-sm text-red-700">{error.message}</p>
                    </div>
                )}

                {/* Promotion trouvée mais inactive */}
                {promotion && !promotion.isActive && !error && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
                        <Icon iconName="AlertTriangle" size={18} className="text-amber-500 mt-0.5 flex-shrink-0" />
                        <p className="text-sm text-amber-700">
                            Ce code promo n'est plus valide ou a déjà été utilisé.
                        </p>
                    </div>
                )}
            </div>
        </Card>
    )
}
